import type { RoomSelection } from '@interfaces/expedia/property';
import { create } from 'zustand';
import { toHotelDistanceSliderValue } from '@v2/views/SearchHotel/hotel-distance';

// Function to get default distance based on device type
export const getDefaultDistance = (): number[] => {
  // Check if we're in a browser environment
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
  ) {
    // Use media query to detect mobile devices (max-width: 1024px)
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    return isMobile ? [5] : [10];
  }
  // Default to web value during SSR or test environment
  return [10];
};

export interface SearchHotelState {
  isMapFullScreen: boolean;
  hasPricePerTravelerChanged: boolean;
  hasDistanceChanged: boolean;
  setHotelFilters: (filters: Partial<SearchHotelState['hotelFilters']>) => void;
  setHotelRange: (
    filters: Partial<
      Pick<SearchHotelState['hotelFilters'], 'pricePerTraveler' | 'distance'>
    >
  ) => void;
  reset: () => void;
  setHotelSelectedInMap: (val: number | null, isOpen: boolean) => void;
  resetHotelFilters: () => void;
  hotelFilters: {
    featureHotel: string[];
    pricePerTraveler: number[];
    rating: string[];
    sortBy: string;
    distance: number[];
    query: string;
  };

  hotelDrawerSelected: {
    isOpen: boolean;
    hotelId: number | null;
  };

  roomSelections: RoomSelection[];
  selectedHotelId: number | null;
  setHotelRoomSelection: (val: RoomSelection) => void;
  removeHotelRoomSelection: (index: number) => void;
  selectableOccupancies: string[];
}

export const DEFAULT_DISTANCE = getDefaultDistance();
export const DEFAULT_PRICE_PER_TRAVELER = [0, 6500];
export const DEFAULT_HOTEL_SORT = 'distance-low-high';

const initValues = {
  roomSelections: [],
  selectedHotelId: null as number | null,
  hasPricePerTravelerChanged: false,
  hasDistanceChanged: false,
  isMapFullScreen: false,
  hotelFilters: {
    featureHotel: [],
    pricePerTraveler: DEFAULT_PRICE_PER_TRAVELER,
    rating: [],
    sortBy: DEFAULT_HOTEL_SORT,
    distance: DEFAULT_DISTANCE,
    query: '',
  },
  hotelDrawerSelected: {
    isOpen: false,
    hotelId: null,
  },
  selectableOccupancies: [],
};

export const useSearchHotelStore = create<SearchHotelState>((set, get) => ({
  ...initValues,

  setHotelSelectedInMap: (val, isOpen) =>
    set({
      hotelDrawerSelected: {
        hotelId: val,
        isOpen: isOpen ?? get().hotelDrawerSelected.isOpen,
      },
    }),

  setHotelFilters: (filters) => {
    set((state) => ({
      hotelFilters: {
        ...state.hotelFilters,
        ...filters,
        ...('distance' in filters && filters.distance !== undefined
          ? { distance: toHotelDistanceSliderValue(filters.distance) }
          : {}),
      },
    }));
  },
  setHotelRoomSelection: (room) => {
    set((state) => ({
      roomSelections: [...state.roomSelections, room],
    }));
  },

  removeHotelRoomSelection: (index) => {
    set((state) => {
      const newSelections = [...state.roomSelections];
      newSelections.splice(index, 1);
      return { roomSelections: newSelections };
    });
  },

  setHotelRange: (filters) => {
    set((state) => {
      const distance =
        'distance' in filters && filters.distance !== undefined
          ? toHotelDistanceSliderValue(filters.distance)
          : undefined;
      const newState = {
        ...state,
        hotelFilters: {
          ...state.hotelFilters,
          ...filters,
          ...(distance ? { distance } : {}),
        },
      };

      if (distance) {
        newState.hasDistanceChanged = true;
      }

      if ('pricePerTraveler' in filters) {
        newState.hasPricePerTravelerChanged = true;
      }

      return newState;
    });
  },
  resetHotelFilters: () => {
    set(() => ({
      hasDistanceChanged: false,
      hasPricePerTravelerChanged: false,
      hotelFilters: {
        ...initValues.hotelFilters,
        distance: getDefaultDistance(), // Use dynamic default distance
      },
    }));
  },
  reset: () => {
    set({
      ...initValues,
      hotelFilters: {
        ...initValues.hotelFilters,
        distance: getDefaultDistance(), // Use dynamic default distance
      },
    });
  },
}));
